import React from 'react';
import './deeplinkbanner.css';
import { getContextMessage, getAppLabel, getPlayStoreUrl, getAppStoreUrl, getStoreUrl, getDeepLinkUrl } from '../../utils/deeplink';

const QR_CODE_API = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=';

function StoreIcon({ type }) {
  if (type === 'android') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.523 16.572l3.724-6.452a.75.75 0 0 0-.268-1.02l-1.782-1.03a.75.75 0 0 0-1.02.268l-3.724 6.451-3.724-6.45a.75.75 0 0 0-1.02-.27L5.99 7.9a.75.75 0 0 0-.27 1.02l3.724 6.452-3.724 6.451a.75.75 0 0 0 .269 1.02l1.782 1.03a.75.75 0 0 0 1.02-.268l3.724-6.452 3.724 6.452a.75.75 0 0 0 1.02.268l1.782-1.03a.75.75 0 0 0 .27-1.02z"/>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.68-.73 1.65-1.16 2.56-1.17.26 1.24-.33 2.46-1.02 3.2-.66.75-1.6 1.2-2.5 1.17-.24-1.16.36-2.28 1.24-3.2z"/>
    </svg>
  );
}

export default function DeepLinkBanner({ type, identifier, platform, appOpened, timedOut, entityData, onOpenApp }) {
  const deepLinkUrl = getDeepLinkUrl(type, identifier);
  const qrUrl = `${QR_CODE_API}${encodeURIComponent(deepLinkUrl)}`;
  const storeUrl = getStoreUrl(platform);
  const storeLabel = platform === 'android' ? 'Download for Android' : platform === 'ios' ? 'Download for iOS' : null;
  const entityName = entityData?.name || null;
  const entityImage = entityData?.imageUrl || entityData?.image || entityData?.imageurl || entityData?.photoUrl || entityData?.photoURL || entityData?.avatar || entityData?.profileImage || null;
  const entityDescription = entityData?.description || null;
  const entityAbout = entityData?.about || null;

  return (
    <div className="deeplink-wrapper">
      <div className="deeplink-card">
        {entityImage ? (
          <img className="deeplink-entity-image" src={entityImage} alt={entityName || identifier} />
        ) : (
          <img
            className="deeplink-logo"
            src={`${process.env.PUBLIC_URL}/solochatLogo.png`}
            alt="SoloChat"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}

        <div className="deeplink-badge">{getAppLabel(type)}</div>

        <h1 className="deeplink-title">{entityName || getContextMessage(type, identifier)}</h1>
        <p className="deeplink-subtitle">
          {(type === 'group' || type === 'room') && entityDescription
            ? entityDescription
            : type === 'chat' && entityAbout
            ? entityAbout
            : entityName
            ? `Connect with ${entityName} on SoloChat Messenger.`
            : 'Connect with people around you with SoloChat providing you a unique messaging experience.'}
        </p>

        <button
          className="deeplink-open-btn"
          onClick={onOpenApp}
          disabled={appOpened || timedOut}
        >
          {appOpened ? 'Opening SoloChat...' : timedOut ? 'Try Again' : 'Open in App'}
        </button>

        {platform !== 'desktop' && storeUrl && storeUrl !== '#' && (
          <a
            className="deeplink-open-btn"
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', display: 'block' }}
          >
            {storeLabel || 'Download'}
          </a>
        )}

        {timedOut && (
          <div className="deeplink-not-installed">
            {platform === 'android'
              ? 'SoloChat not installed? Download from the link above.'
              : 'SoloChat not installed? Download from the link above.'}
          </div>
        )}

        {!appOpened && !timedOut && platform === 'desktop' && (
          <p className="deeplink-fallback-msg">
            Don't have SoloChat? Download from the stores below.
          </p>
        )}

        {platform === 'desktop' && (
          <div className="deeplink-store-links">
            <a
              className="deeplink-store-link"
              href={getPlayStoreUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <StoreIcon type="android" />
              Google Play
            </a>
            {getAppStoreUrl() !== '#' && (
              <a
                className="deeplink-store-link"
                href={getAppStoreUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StoreIcon type="ios" />
                App Store
              </a>
            )}
          </div>
        )}

        {platform === 'desktop' && (
          <div className="deeplink-qr-section">
            <p>Scan with your phone to open in SoloChat</p>
            <div className="deeplink-qr-code">
              <img src={qrUrl} alt="QR Code to open in SoloChat" />
            </div>
            <p className="deeplink-fallback-msg" style={{ marginTop: '1rem' }}>
              Or download from{' '}
              <a href={getPlayStoreUrl()} target="_blank" rel="noopener noreferrer">Google Play</a>
              {getAppStoreUrl() !== '#' && (
                <span> / <a href={getAppStoreUrl()} target="_blank" rel="noopener noreferrer">App Store</a></span>
              )}
            </p>
          </div>
        )}
      </div>

      <div className="deeplink-footer">
        <a href="https://solochat.me">solochat.me</a>
      </div>
    </div>
  );
}
